import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import Global from '../../store/global-selectors';
import { useWalletBalance } from '../../wallet/contexts/wallet-data-context-provider';
import { useDeriveSubscriptionInfo } from '../../pages/settings/payment-and-subscription/hooks/use-derive-subscription-info';
import { useUserHasPurchasingRole } from '../../hooks/use-user-role';
import { AddFundsLink } from './add-funds-link';
import { useWallet } from '../../wallet/hooks/use-wallet';

import messages from './messages';
import style from './style.module.less';
import { FormattedBalance } from './formatted-balance';

const getContainerBackgroundStyle = (loading, balance, compareChargeAmount) => {
  if (loading || compareChargeAmount === 0) {
    return '';
  }

  if (balance >= compareChargeAmount) {
    return style.sufficient;
  }

  return style.insufficient;
};

export const WalletBalanceInfo = ({
  walletId: walletIdProp,
  compareChargeAmount = 0,
  isFullSize = false,
  isManageFunds = false,
  trackingId,
}) => {
  const { walletId } = useWallet(walletIdProp) ?? {};
  const { balance, loading } = useWalletBalance(walletId);
  const userHasPurchasingRole = useUserHasPurchasingRole();
  const canAddFunds = userHasPurchasingRole && walletId;
  const planInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const { isTrialExpired } = planInfo;
  const { inactiveAccount } = useDeriveSubscriptionInfo(planInfo);
  const showAddFundsLink = canAddFunds && !isTrialExpired && !inactiveAccount;

  const insufficientBalance = balance?.value < compareChargeAmount;
  const combineFullStyle = (baseStyle, ...additionalFullStyles) => {
    if (!isFullSize) {
      return baseStyle;
    }
    return classnames(baseStyle, ...additionalFullStyles, style.fullSize);
  };

  const containerClassNames = combineFullStyle(style.container, getContainerBackgroundStyle(loading, balance?.value, compareChargeAmount));

  return (
    <div className={containerClassNames}>
      <div>
        <div className={combineFullStyle(style.text)}>
          <FormattedMessage {...messages.shipStationBalance} />
        </div>
        {isFullSize && (
          <div className={style.subtext}>
            <FormattedMessage {...messages.subText} />
          </div>
        )}
      </div>
      {loading && <LoadingIndicator className={combineFullStyle(style.loading)} />}
      {!loading && (
        <div className={combineFullStyle(style.balance)}>
          <div
            className={combineFullStyle(style.amount, {
              [style.insufficient]: insufficientBalance,
            })}
          >
            {insufficientBalance && isFullSize && (
              <Tooltip>
                <div className={style['error-icon']}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </div>
                <TooltipContent>
                  <FormattedMessage {...messages.insuffcientFundsError} />
                </TooltipContent>
              </Tooltip>
            )}
            <FormattedBalance balance={balance} />
          </div>
          {
            // If user has Account Manager role they can SEE the wallet balance but not add funds to it
            showAddFundsLink && (
              <AddFundsLink className={style.link} isManageFunds={isManageFunds} trackingId={trackingId} walletId={walletId} />
            )
          }
        </div>
      )}
    </div>
  );
};

WalletBalanceInfo.propTypes = {
  walletId: PropTypes.string,
  compareChargeAmount: PropTypes.number,
  isFullSize: PropTypes.bool,
  isManageFunds: PropTypes.bool,
  trackingId: PropTypes.string.isRequired,
};
